import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Wrench,
  Plus,
  MapPin,
  Camera,
  ThumbsUp,
  Clock,
  CheckCircle,
  AlertCircle,
  Lightbulb,
  Trash2,
  Construction,
  RefreshCw,
  Map,
  List,
  Navigation,
  Car
} from 'lucide-react';

const issueTypes = [
  { value: 'pothole', label: 'Pothole', icon: AlertCircle, color: 'bg-orange-500' },
  { value: 'streetlight', label: 'Broken Streetlight', icon: Lightbulb, color: 'bg-yellow-500' },
  { value: 'dumping', label: 'Illegal Dumping', icon: Trash2, color: 'bg-red-500' },
  { value: 'signage', label: 'Damaged Signage', icon: Construction, color: 'bg-blue-500' },
  { value: 'drainage', label: 'Drainage/Flooding', icon: AlertCircle, color: 'bg-cyan-500' },
  { value: 'road_damage', label: 'Road Damage', icon: Car, color: 'bg-purple-500' },
  { value: 'other', label: 'Other Issue', icon: Wrench, color: 'bg-slate-500' }
];

const statusConfig = {
  todo: { label: 'To Do', color: 'bg-slate-500', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-plp-gold text-plp-blue', icon: Wrench },
  done: { label: 'Done', color: 'bg-emerald-500', icon: CheckCircle }
};

// Custom map icons
const createIssueIcon = (color) => L.divIcon({
  className: 'custom-issue-icon',
  html: `<div style="background-color: ${color}; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12]
});

const issueIconColors = {
  pothole: '#f97316',
  streetlight: '#eab308',
  dumping: '#ef4444',
  signage: '#3b82f6',
  drainage: '#06b6d4',
  road_damage: '#a855f7',
  other: '#6b7280'
};

const FixItForwardPage = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    issue_type: '',
    latitude: 25.0443,
    longitude: -77.3504,
    photo_url: ''
  });

  const fetchIssues = async () => {
    try {
      const response = await axios.get(`${API}/issues`);
      setIssues(response.data);
    } catch (error) {
      console.error('Failed to fetch issues:', error);
      toast.error('Failed to load issues');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIssues();
    // Get user location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        setFormData(prev => ({
          ...prev,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        }));
      });
    }
  }, []);

  const handleSubmit = async () => {
    if (!formData.title || !formData.issue_type) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API}/issues`, formData);
      toast.success('Issue reported successfully!');
      setShowDialog(false);
      setFormData({
        title: '',
        description: '',
        issue_type: '',
        latitude: formData.latitude,
        longitude: formData.longitude,
        photo_url: ''
      });
      fetchIssues();
    } catch (error) {
      toast.error('Failed to submit issue');
    }
  };

  const handleUpvote = async (issueId) => {
    try {
      await axios.post(`${API}/issues/${issueId}/upvote`);
      fetchIssues();
    } catch (error) {
      toast.error('Failed to upvote');
    }
  };

  const filteredIssues = activeTab === 'all' 
    ? issues 
    : issues.filter(issue => issue.status === activeTab);

  const getIssueCounts = () => ({
    all: issues.length,
    todo: issues.filter(i => i.status === 'todo').length,
    in_progress: issues.filter(i => i.status === 'in_progress').length,
    done: issues.filter(i => i.status === 'done').length
  });

  const counts = getIssueCounts();

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 flex items-center gap-3">
              <Wrench className="w-10 h-10 text-amber-500" />
              Fix It Forward
            </h1>
            <p className="mt-2 text-slate-600">
              Report infrastructure issues and track progress. Together we make Killarney better.
            </p>
          </div>
          
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button className="bg-plp-blue hover:bg-plp-blue-light text-white" data-testid="report-issue-btn">
                <Plus className="w-4 h-4 mr-2" />
                Report Issue
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg" data-testid="report-issue-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading">Report an Issue</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="issue_type">Issue Type *</Label>
                  <Select value={formData.issue_type} onValueChange={(v) => setFormData(prev => ({ ...prev, issue_type: v }))}>
                    <SelectTrigger data-testid="issue-type-select">
                      <SelectValue placeholder="Select issue type" />
                    </SelectTrigger>
                    <SelectContent>
                      {issueTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <span className="flex items-center gap-2">
                            <type.icon className="w-4 h-4" />
                            {type.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.issue_type === 'other' && (
                    <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Please describe your issue in detail in the Title and Details fields below
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="title">{formData.issue_type === 'other' ? 'Issue Type Description *' : 'Title *'}</Label>
                  <Input
                    id="title"
                    placeholder={formData.issue_type === 'other' ? "Describe what type of issue this is" : "Brief description of the issue"}
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    data-testid="issue-title-input"
                  />
                </div>
                <div>
                  <Label htmlFor="description">{formData.issue_type === 'other' ? 'Full Details *' : 'Details'}</Label>
                  <Textarea
                    id="description"
                    placeholder={formData.issue_type === 'other' ? "Please provide complete details about this issue, including location specifics and what needs to be fixed..." : "Provide more details about the issue..."}
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={formData.issue_type === 'other' ? 4 : 3}
                    data-testid="issue-description-input"
                  />
                </div>
                <div>
                  <Label htmlFor="photo_url">Photo URL (optional)</Label>
                  <Input
                    id="photo_url"
                    placeholder="https://..."
                    value={formData.photo_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, photo_url: e.target.value }))}
                    data-testid="issue-photo-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Upload your photo to a service and paste the URL here</p>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
                  <MapPin className="w-4 h-4" />
                  Location will be captured automatically
                </div>
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" onClick={() => setShowDialog(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button onClick={handleSubmit} className="flex-1 bg-plp-blue hover:bg-plp-blue-light" data-testid="submit-issue-btn">
                    Submit Report
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Issues', value: counts.all, color: 'border-l-slate-500' },
            { label: 'To Do', value: counts.todo, color: 'border-l-slate-400' },
            { label: 'In Progress', value: counts.in_progress, color: 'border-l-amber-500' },
            { label: 'Fixed', value: counts.done, color: 'border-l-emerald-500' }
          ].map((stat) => (
            <Card key={stat.label} className={`bg-white border-l-4 ${stat.color}`}>
              <CardContent className="p-4">
                <p className="text-2xl font-heading font-bold text-slate-900">{stat.value}</p>
                <p className="text-sm text-slate-500">{stat.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabs & Issue Board */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <TabsList className="bg-white border">
              <TabsTrigger value="all" data-testid="tab-all">All ({counts.all})</TabsTrigger>
              <TabsTrigger value="todo" data-testid="tab-todo">To Do ({counts.todo})</TabsTrigger>
              <TabsTrigger value="in_progress" data-testid="tab-in-progress">In Progress ({counts.in_progress})</TabsTrigger>
              <TabsTrigger value="done" data-testid="tab-done">Done ({counts.done})</TabsTrigger>
            </TabsList>
            <div className="flex items-center gap-2">
              <div className="bg-white border rounded-lg p-1 flex">
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className={viewMode === 'list' ? 'bg-plp-blue' : ''}
                  data-testid="view-list-btn"
                >
                  <List className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'map' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('map')}
                  className={viewMode === 'map' ? 'bg-plp-blue' : ''}
                  data-testid="view-map-btn"
                >
                  <Map className="w-4 h-4" />
                </Button>
              </div>
              <Button variant="ghost" size="sm" onClick={fetchIssues} data-testid="refresh-issues">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>

          <TabsContent value={activeTab} className="mt-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 text-plp-blue animate-spin" />
              </div>
            ) : viewMode === 'map' ? (
              /* Map View */
              <div className="space-y-4">
                <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
                  <CardContent className="p-4 flex items-center gap-3">
                    <Car className="w-6 h-6 text-amber-500" />
                    <div>
                      <p className="font-medium text-slate-800">Waze for Roads</p>
                      <p className="text-sm text-slate-600">View reported infrastructure issues on the map. Click markers for details.</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white overflow-hidden" style={{ height: '500px' }}>
                  <MapContainer
                    center={[25.0443, -77.3504]}
                    zoom={14}
                    style={{ height: '100%', width: '100%' }}
                    data-testid="issues-map"
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    />
                    {filteredIssues.map((issue) => (
                      <Marker
                        key={issue.id}
                        position={[issue.latitude, issue.longitude]}
                        icon={createIssueIcon(issueIconColors[issue.issue_type] || '#6b7280')}
                      >
                        <Popup>
                          <div className="min-w-[200px]">
                            <Badge className={`${statusConfig[issue.status].color} text-white mb-2`}>
                              {statusConfig[issue.status].label}
                            </Badge>
                            <h3 className="font-bold text-slate-900">{issue.title}</h3>
                            <p className="text-sm text-slate-600 mt-1">{issue.description}</p>
                            <p className="text-xs text-slate-400 mt-2">
                              {new Date(issue.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </MapContainer>
                </Card>
                {/* Map Legend */}
                <Card className="bg-white">
                  <CardContent className="p-4">
                    <p className="text-sm font-medium text-slate-700 mb-3">Issue Types</p>
                    <div className="flex flex-wrap gap-4">
                      {issueTypes.map(type => (
                        <div key={type.value} className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded-full ${type.color}`}></div>
                          <span className="text-sm text-slate-600">{type.label}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : filteredIssues.length === 0 ? (
              <Card className="bg-white">
                <CardContent className="py-12 text-center">
                  <Wrench className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No issues found</p>
                  <Button onClick={() => setShowDialog(true)} variant="link" className="text-plp-blue mt-2">
                    Report the first issue
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredIssues.map((issue) => {
                  const typeConfig = issueTypes.find(t => t.value === issue.issue_type);
                  const status = statusConfig[issue.status];
                  
                  return (
                    <Card key={issue.id} className="bg-white hover:shadow-md transition-shadow" data-testid={`issue-card-${issue.id}`}>
                      <CardContent className="p-4">
                        {/* Photo */}
                        {issue.photo_url && (
                          <div className="aspect-video rounded-lg overflow-hidden mb-4 bg-slate-100">
                            <img src={issue.photo_url} alt={issue.title} className="w-full h-full object-cover" />
                          </div>
                        )}
                        
                        {/* Header */}
                        <div className="flex items-start justify-between gap-2 mb-3">
                          <div className="flex items-center gap-2">
                            {typeConfig && (
                              <div className={`w-8 h-8 ${typeConfig.color} rounded-lg flex items-center justify-center`}>
                                <typeConfig.icon className="w-4 h-4 text-white" />
                              </div>
                            )}
                            <Badge className={`${status.color} text-white`}>
                              <status.icon className="w-3 h-3 mr-1" />
                              {status.label}
                            </Badge>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUpvote(issue.id)}
                            className="text-slate-500 hover:text-plp-blue"
                            data-testid={`upvote-${issue.id}`}
                          >
                            <ThumbsUp className="w-4 h-4 mr-1" />
                            {issue.upvotes}
                          </Button>
                        </div>

                        {/* Content */}
                        <h3 className="font-heading font-semibold text-slate-900">{issue.title}</h3>
                        {issue.description && (
                          <p className="text-sm text-slate-600 mt-2 line-clamp-2">{issue.description}</p>
                        )}

                        {/* Footer */}
                        <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
                          <span className="text-xs text-slate-400">
                            {new Date(issue.created_at).toLocaleDateString()}
                          </span>
                          {issue.completed_by && (
                            <span className="text-xs text-emerald-600 font-medium">
                              Fixed by: {issue.completed_by}
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Info Section */}
        <Card className="mt-8 bg-plp-blue/5 border-plp-blue/20">
          <CardContent className="p-6">
            <h3 className="font-heading font-bold text-lg text-plp-blue mb-2">How Fix It Forward Works</h3>
            <div className="grid md:grid-cols-3 gap-4 text-sm text-slate-600">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-plp-blue rounded-full flex items-center justify-center text-white font-bold">1</div>
                <p><strong>Report:</strong> Submit an issue with photo and location</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-plp-blue rounded-full flex items-center justify-center text-white font-bold">2</div>
                <p><strong>Track:</strong> Watch your report move through the system</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-plp-blue rounded-full flex items-center justify-center text-white font-bold">3</div>
                <p><strong>Celebrate:</strong> See it marked as fixed with proof</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FixItForwardPage;
